/**
 * Partner Intelligence Panel
 * ===========================
 *
 * Reads a selected partner from the existing `/sales/partners/summary`
 * (now enriched with `total_covers` + `service_mode_breakdown[]`) plus
 * `/sales/time-intelligence`, `/sales/items/top`, and the in-tab
 * `/sales/partners/trends` payload that the page already fetches.
 *
 * Goal: an operator understands an entire partner/channel in <10s.
 *
 * Compose-don't-build — no new endpoints, no new route, no new sidebar
 * entry. Renders inside the Partners tab above the existing card grid.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  CurrencyInr, Receipt, Users, ChartLineUp, Clock,
  Package, Tag, CaretDown, Handshake, Warning, ShieldCheck, Eye,
} from '@phosphor-icons/react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { salesApi } from '../lib/api';
import DeltaPill from './DeltaPill';

/* ── Format helpers (kept local so the panel is portable) ── */
const fmtINR = (v) =>
  v != null ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '0';
const fmtShort = (v) => {
  if (!v && v !== 0) return '0';
  if (Math.abs(v) >= 1e7) return `${(v / 1e7).toFixed(1)}Cr`;
  if (Math.abs(v) >= 1e5) return `${(v / 1e5).toFixed(1)}L`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return Number(v).toFixed(0);
};
const fmtPct = (v, d = 1) => (v == null ? '—' : `${Number(v).toFixed(d)}%`);
const hourLabel = (h) => {
  if (h == null) return '—';
  const am = h < 12;
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh} ${am ? 'AM' : 'PM'}`;
};

/* Service-mode palette — same as TimeOfDayBento for consistency. */
const SERVICE_MODE_COLOR = {
  dine_in:  '#22d3ee',  // cyan
  delivery: '#f59e0b',  // amber
  takeaway: '#34d399',  // emerald
  other:    '#71717a',  // zinc
};
const SERVICE_MODE_LABEL = {
  dine_in: 'Dine-in', delivery: 'Delivery', takeaway: 'Takeaway', other: 'Other',
};

/* ── PP pill ─ percentage-points change, the statistically correct
   unit for rate / share deltas (contribution %, discount rate %,
   share %). Same colour grammar as DeltaPill. */
const PpPill = ({ pp, direction, title, testid }) => {
  if (pp == null || direction == null) return null;
  const dir = direction === 'flat' && Math.abs(pp) < 0.05 ? 'flat' : direction;
  const tone = dir === 'up'   ? 'bg-emerald-500/10 text-emerald-400'
            : dir === 'down' ? 'bg-rose-500/10 text-rose-400'
            : dir === 'new'  ? 'bg-cyan-500/10 text-cyan-400'
            :                  'bg-zinc-500/10 text-zinc-500';
  const arrow = dir === 'up' ? '\u25B2' : dir === 'down' ? '\u25BC' : '';
  const label = dir === 'new' ? 'NEW' : `${arrow}${pp >= 0 ? '+' : ''}${pp.toFixed(1)}pp`;
  return (
    <span
      className={`inline-flex items-center rounded ${tone} text-[10px] px-1 py-0.5 gap-0.5 font-mono`}
      title={title}
      data-testid={testid}
      data-direction={dir}
      data-pp={pp}
    >{label}</span>
  );
};

/* ── KPI tile ── */
const Kpi = ({ icon: Icon, label, value, sub, delta, testid }) => (
  <div
    className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3"
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
      {Icon && <Icon size={11} weight="fill" className="text-cyan-400" />}
      {label}
    </div>
    <div className="mt-1 font-mono text-lg text-zinc-100 leading-tight flex items-baseline gap-1.5 flex-wrap">
      <span>{value}</span>
      {delta}
    </div>
    {sub && <div className="mt-0.5 text-[11px] text-zinc-500">{sub}</div>}
  </div>
);

/* ── Section header ── */
const SectionHeader = ({ title, sub }) => (
  <div className="flex items-baseline justify-between mb-2">
    <h4 className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">{title}</h4>
    {sub && <span className="text-[10px] text-zinc-500">{sub}</span>}
  </div>
);

/* ────────────────────────────────────────────────────────────── */

export const PartnerIntelligencePanel = ({
  partnerId,
  partnerName,
  orgId,
  outletId,
  dateFrom,
  dateTo,
  summary,        // entry from /sales/partners/summary for this partner
  trends,         // /sales/partners/trends payload (already in parent state)
  comparison,     // top-level comparison block from partners/summary
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [timeIntel, setTimeIntel] = useState(null);
  const [prevTimeIntel, setPrevTimeIntel] = useState(null);
  const [topItems, setTopItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Hour-of-day filter for the Time Intelligence block. Both bounds
  // are INCLUSIVE (e.g. start=19, end=21 means hours 19, 20, 21).
  // `null` = no filter selected yet — defaults populated once the
  // panel knows the peak window for this partner.
  const [hourStart, setHourStart] = useState(null);
  const [hourEnd, setHourEnd]     = useState(null);
  const [hourFilterActive, setHourFilterActive] = useState(false);

  /* Fetch partner-scoped time + items data. Parent already loaded
     summary + trends; we only call what isn't in the page state. */
  useEffect(() => {
    if (!partnerId || !orgId || !dateFrom || !dateTo) return;
    let abort = false;
    setLoading(true);
    (async () => {
      try {
        // Items + Partners Summary use date_from/date_to.
        // Time Intelligence uses start_date/end_date (Phase 5.5
        // convention) — FastAPI silently ignores unknown query
        // params, so the wrong names result in an all-time query
        // and an inflated order count vs the rest of the panel.
        const baseTime  = { org_id: orgId, start_date: dateFrom, end_date: dateTo };
        const baseItems = { org_id: orgId, date_from: dateFrom, date_to: dateTo };
        if (outletId) { baseTime.outlet_id = outletId; baseItems.outlet_id = outletId; }
        // For "All partners" mode we omit partner_id so the top items
        // endpoint returns the outlet-wide leaders for the window.
        // Top items carries `include_comparison` so each row gets the
        // standard {revenue_change_pct, revenue_change_direction} pair.
        const itemsQuery = { ...baseItems, limit: 5, sort_by: 'revenue', include_comparison: true };
        if (partnerId && partnerId !== '__ALL__') itemsQuery.partner_id = partnerId;
        // Resolve prev-window dates from the summary's comparison block
        // (single source of truth). If no comparison was resolved we
        // simply skip the prev time-intel call.
        const prevTimeReq = comparison?.previous_from && comparison?.previous_to
          ? salesApi.getTimeIntelligence({
              ...baseTime,
              start_date: comparison.previous_from,
              end_date:   comparison.previous_to,
            }).catch(() => null)
          : Promise.resolve(null);
        const [timeRes, itemsRes, prevTimeRes] = await Promise.all([
          // time-intelligence isn't partner-filtered on the request — it
          // returns hourly_demand[].partners[] and we slice client-side.
          salesApi.getTimeIntelligence
            ? salesApi.getTimeIntelligence(baseTime)
            : (await import('../lib/api')).default.get(
                `/sales/time-intelligence?${new URLSearchParams(baseTime).toString()}`
              ),
          salesApi.getTopItems(itemsQuery),
          prevTimeReq,
        ]);
        if (abort) return;
        setTimeIntel(timeRes.data);
        setTopItems(itemsRes.data?.items || []);
        setPrevTimeIntel(prevTimeRes?.data || null);
      } catch (e) {
        if (!abort) {
          setTimeIntel(null);
          setTopItems([]);
          setPrevTimeIntel(null);
        }
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [partnerId, orgId, outletId, dateFrom, dateTo,
      comparison?.previous_from, comparison?.previous_to]);

  /* ── Derived data ──────────────────────────────────────────── */

  // A. KPIs come straight from summary
  const kpis = useMemo(() => {
    if (!summary) return null;
    const discountRate = summary.gross_sales > 0
      ? (summary.discounts / summary.gross_sales) * 100 : 0;
    return {
      revenue: summary.net_sales || 0,
      orders:  summary.orders || 0,
      aov:     summary.avg_order_value || 0,
      covers:  summary.total_covers || 0,
      // `null` is sentinel for "All partners" mode — UI renders "—"
      // because share-of-self is always 100% (noise, not signal).
      contribution: summary.contribution_pct == null ? null : summary.contribution_pct,
      discounts: summary.discounts || 0,
      discount_rate_pct: discountRate,
      gross_sales: summary.gross_sales || 0,
    };
  }, [summary]);

  // B. Channel mix from summary
  const channelMix = useMemo(() => {
    const rows = summary?.service_mode_breakdown || [];
    return rows.map(r => ({
      name: SERVICE_MODE_LABEL[r.service_mode] || r.service_mode,
      mode: r.service_mode,
      value: r.net_sales,
      orders: r.orders,
      covers: r.covers,
      share_pct: r.share_pct,
      previous_net_sales: r.previous_net_sales,
      previous_share_pct: r.previous_share_pct,
      revenue_change_pct: r.revenue_change_pct,
      revenue_change_direction: r.revenue_change_direction,
      share_change_pp: r.share_change_pp,
      share_change_direction: r.share_change_direction,
      fill: SERVICE_MODE_COLOR[r.service_mode] || '#71717a',
    }));
  }, [summary]);

  // C. Time intelligence — partner-scoped hourly + peak. In "all
  // partners" mode, sum across every partner row per hour.
  const hourly = useMemo(() => {
    if (!timeIntel?.hourly_demand) return [];
    const allMode = partnerId === '__ALL__';
    return timeIntel.hourly_demand.map(h => {
      let orders = 0, revenue = 0;
      if (allMode) {
        for (const p of (h.partners || [])) {
          orders  += p.orders  || 0;
          revenue += p.revenue || 0;
        }
      } else {
        const row = (h.partners || []).find(p => p.partner_id === partnerId);
        orders  = row?.orders  || 0;
        revenue = row?.revenue || 0;
      }
      return {
        hour: h.hour,
        label: h.label || `${String(h.hour).padStart(2, '0')}:00`,
        orders, revenue,
      };
    });
  }, [timeIntel, partnerId]);

  // Same shape from the previous-window time-intelligence payload.
  const prevHourly = useMemo(() => {
    if (!prevTimeIntel?.hourly_demand) return [];
    const allMode = partnerId === '__ALL__';
    return prevTimeIntel.hourly_demand.map(h => {
      let orders = 0, revenue = 0;
      if (allMode) {
        for (const p of (h.partners || [])) {
          orders  += p.orders  || 0;
          revenue += p.revenue || 0;
        }
      } else {
        const row = (h.partners || []).find(p => p.partner_id === partnerId);
        orders  = row?.orders  || 0;
        revenue = row?.revenue || 0;
      }
      return { hour: h.hour, orders, revenue };
    });
  }, [prevTimeIntel, partnerId]);

  const peakInfo = useMemo(() => {
    if (!hourly.length) return null;
    const max = hourly.reduce((a, b) => (b.orders > a.orders ? b : a), hourly[0]);
    if (max.orders === 0) return null;
    // 3-hour peak window
    let bestStart = 0, bestSum = -1;
    for (let i = 0; i + 3 <= 24; i++) {
      const s = hourly.slice(i, i + 3).reduce((a, r) => a + r.orders, 0);
      if (s > bestSum) { bestSum = s; bestStart = i; }
    }
    return {
      peak_hour: max.hour,
      peak_orders: max.orders,
      window_start: bestStart,
      window_end: bestStart + 3,
      window_orders: bestSum,
    };
  }, [hourly]);

  // Previous-period peak — used to compute deltas on Peak Hour and
  // Peak 3h Window pills. Same algorithm as `peakInfo`.
  const prevPeakInfo = useMemo(() => {
    if (!prevHourly.length) return null;
    const totals = prevHourly.reduce((a, b) => a + (b.orders || 0), 0);
    if (totals === 0) return null;
    const peakHourOrders = prevHourly[peakInfo?.peak_hour ?? 0]?.orders ?? 0;
    let bestSum = 0;
    if (peakInfo != null) {
      bestSum = prevHourly
        .filter(h => h.hour >= peakInfo.window_start && h.hour < peakInfo.window_end)
        .reduce((a, r) => a + (r.orders || 0), 0);
    }
    return {
      peak_hour_orders: peakHourOrders,
      window_orders: bestSum,
    };
  }, [prevHourly, peakInfo]);

  // One-line time narrative — surfaces concentrated growth band.
  const timeNarrative = useMemo(() => {
    if (!hourly.length || !prevHourly.length) return null;
    const curTot  = hourly.reduce((a, r) => a + (r.orders  || 0), 0);
    const prevTot = prevHourly.reduce((a, r) => a + (r.orders || 0), 0);
    if (curTot < 30 || prevTot < 30) return null;
    const deltas = hourly.map((h, i) => (h.orders || 0) - (prevHourly[i]?.orders || 0));
    if (deltas.every(d => d <= 0)) return null;
    let bestStart = 0, bestSum = -1;
    for (let i = 0; i + 3 <= 24; i++) {
      const s = deltas.slice(i, i + 3).reduce((a, x) => a + x, 0);
      if (s > bestSum) { bestSum = s; bestStart = i; }
    }
    const totalGrowth = deltas.reduce((a, d) => a + Math.max(d, 0), 0) || 1;
    if (bestSum / totalGrowth < 0.40 || bestSum < 5) return null;
    return `Most growth between ${hourLabel(bestStart)}–${hourLabel(bestStart + 3)} (+${bestSum} orders).`;
  }, [hourly, prevHourly]);

  // Reset hour-window state whenever the partner OR date changes —
  // a window that made sense for last partner / last month is
  // misleading for the new slice. Seed defaults from peak so the
  // selector always has a sensible starting point if the user opts in.
  useEffect(() => {
    setHourFilterActive(false);
    if (peakInfo) {
      setHourStart(peakInfo.window_start);
      setHourEnd(peakInfo.window_end - 1); // inclusive
    } else {
      setHourStart(null);
      setHourEnd(null);
    }
  }, [partnerId, dateFrom, dateTo, peakInfo]);

  // Aggregated metrics for the user-chosen hour window. Returns
  // {orders, revenue, aov} or null when no filter is active /
  // bounds are invalid. Pure client-side from the already-fetched
  // hourly array, so the slice updates instantly on dropdown change.
  const windowMetrics = useMemo(() => {
    if (!hourFilterActive) return null;
    if (hourStart == null || hourEnd == null) return null;
    if (hourEnd < hourStart) return null;
    const slice = hourly.filter(h => h.hour >= hourStart && h.hour <= hourEnd);
    const orders  = slice.reduce((a, r) => a + (r.orders  || 0), 0);
    const revenue = slice.reduce((a, r) => a + (r.revenue || 0), 0);
    const aov = orders > 0 ? revenue / orders : 0;
    // Total across the full 24h for this partner — used as the
    // denominator for the share-of-total percentages. Sourced from
    // the SAME `hourly` array so the two numbers always reconcile
    // (using summary.net_sales would drift, since the time-intel
    // endpoint filters source_status=primary while partners summary
    // does not — Phase 7.11 finding).
    const totalOrders  = hourly.reduce((a, r) => a + (r.orders  || 0), 0);
    const totalRevenue = hourly.reduce((a, r) => a + (r.revenue || 0), 0);
    return {
      orders, revenue, aov, hours: slice.length,
      orders_share_pct:  totalOrders  > 0 ? (orders  / totalOrders)  * 100 : 0,
      revenue_share_pct: totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0,
    };
  }, [hourFilterActive, hourStart, hourEnd, hourly]);

  // E. Revenue trend from the parent-supplied trends payload, filtered
  // by partner and aggregated per day. Also derive AOV per day.
  // In "all partners" mode, include every partner row (no filter).
  const revenueTrend = useMemo(() => {
    const rows = trends?.trends || [];
    const allMode = partnerId === '__ALL__';
    const byDate = new Map();
    for (const r of rows) {
      if (!allMode && r.partner_id !== partnerId) continue;
      const cur = byDate.get(r.date) || { date: r.date, revenue: 0, orders: 0 };
      cur.revenue += r.net_sales || 0;
      cur.orders  += r.orders    || 0;
      byDate.set(r.date, cur);
    }
    return Array.from(byDate.values())
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .map(d => ({
        ...d,
        aov: d.orders > 0 ? d.revenue / d.orders : 0,
      }));
  }, [trends, partnerId]);

  if (!partnerId || !summary) return null;

  return (
    <div
      className="rounded-lg border border-cyan-500/20 bg-gradient-to-b from-cyan-500/[0.04] to-transparent overflow-hidden mb-4"
      data-testid="partner-intel-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <Handshake size={16} weight="fill" className="text-cyan-400" />
          <span className="text-sm font-medium text-zinc-100" data-testid="partner-intel-name">
            {partnerName || partnerId}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-cyan-400/80 font-mono">
            Partner Intelligence
          </span>
        </div>
        <button
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
          data-testid="partner-intel-collapse"
        >
          {collapsed ? 'Expand' : 'Collapse'}
          <CaretDown
            size={12}
            className={`transition-transform ${collapsed ? '-rotate-90' : ''}`}
          />
        </button>
      </div>

      {!collapsed && (
        <div className="p-4 space-y-5">
          {/* ── Verdict + Risks strip ──────────────────────────
              Founder-facing health read. Renders only when the
              backend resolved a real verdict (skipped for the
              "All partners" aggregate to avoid summing-of-summaries
              noise). */}
          {summary?.verdict && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3"
                 data-testid="partner-intel-verdict-strip">
              <div
                className={`lg:col-span-2 rounded-lg border p-3 flex items-start gap-3 ${
                  summary.verdict.status === 'healthy'
                    ? 'border-emerald-500/30 bg-emerald-500/[0.04]'
                    : summary.verdict.status === 'watch'
                    ? 'border-amber-500/30 bg-amber-500/[0.04]'
                    : summary.verdict.status === 'risk'
                    ? 'border-rose-500/30 bg-rose-500/[0.05]'
                    : 'border-zinc-500/30 bg-zinc-500/[0.04]'
                }`}
                data-testid="partner-intel-verdict"
                data-status={summary.verdict.status}
              >
                <div className="shrink-0">
                  {summary.verdict.status === 'healthy' && (
                    <ShieldCheck size={22} weight="fill" className="text-emerald-400" />
                  )}
                  {summary.verdict.status === 'watch' && (
                    <Eye size={22} weight="fill" className="text-amber-400" />
                  )}
                  {summary.verdict.status === 'risk' && (
                    <Warning size={22} weight="fill" className="text-rose-400" />
                  )}
                  {summary.verdict.status === 'new' && (
                    <Handshake size={22} weight="fill" className="text-cyan-400" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-[10px] uppercase tracking-wider font-mono font-medium ${
                      summary.verdict.status === 'healthy' ? 'text-emerald-400'
                      : summary.verdict.status === 'watch' ? 'text-amber-400'
                      : summary.verdict.status === 'risk' ? 'text-rose-400'
                      : 'text-cyan-400'
                    }`}>
                      {summary.verdict.status === 'healthy' ? 'Healthy'
                        : summary.verdict.status === 'watch' ? 'Watch'
                        : summary.verdict.status === 'risk' ? 'At Risk'
                        : 'New'}
                    </span>
                    {comparison?.label && (
                      <span className="text-[10px] text-zinc-500 font-mono">
                        vs {comparison.label}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-100 mt-0.5"
                       data-testid="partner-intel-verdict-headline">
                    {summary.verdict.headline}
                  </div>
                  {summary.verdict.bullets?.length > 0 && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] text-zinc-400">
                      {summary.verdict.bullets.map((b, i) => (
                        <span key={i} className="font-mono">{b}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Risks card — only renders when there's at least one risk. */}
              {summary.risks?.length > 0 ? (
                <div className="rounded-lg border border-rose-500/20 bg-rose-500/[0.03] p-3"
                     data-testid="partner-intel-risks">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-rose-400 font-mono font-medium mb-2">
                    <Warning size={11} weight="fill" /> Risks
                  </div>
                  <ul className="space-y-1.5">
                    {summary.risks.map((r, i) => (
                      <li key={r.id || i}
                          className="flex gap-2 text-[12px] text-zinc-200 leading-snug"
                          data-testid={`risk-${r.id}`}>
                        <span className="text-rose-400/70 mt-0.5">•</span>
                        <span>{r.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.03] p-3 flex items-center gap-2"
                     data-testid="partner-intel-no-risks">
                  <ShieldCheck size={14} weight="fill" className="text-emerald-400" />
                  <span className="text-[12px] text-zinc-300">No commercial risks detected.</span>
                </div>
              )}
            </div>
          )}

          {/* ── A. Commercial KPIs ── */}
          <div data-testid="partner-intel-kpis">
            <SectionHeader title="Commercial" sub={dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : ''} />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Kpi icon={CurrencyInr} label="Revenue"
                   value={`₹${fmtShort(kpis.revenue)}`}
                   delta={<DeltaPill
                     direction={summary?.revenue_change_direction}
                     pct={summary?.revenue_change_pct}
                     title={summary?.previous_net_sales != null
                       ? `prev ₹${fmtINR(summary.previous_net_sales)}` : null}
                     testId="kpi-revenue-delta" />}
                   sub={summary?.previous_net_sales != null
                     ? `prev ₹${fmtShort(summary.previous_net_sales)}`
                     : `₹${fmtINR(kpis.revenue)}`}
                   testid="kpi-revenue" />
              <Kpi icon={Receipt} label="Orders"
                   value={fmtINR(kpis.orders)}
                   delta={<DeltaPill
                     direction={summary?.orders_change_direction}
                     pct={summary?.orders_change_pct}
                     title={summary?.previous_orders != null
                       ? `prev ${summary.previous_orders}` : null}
                     testId="kpi-orders-delta" />}
                   sub={summary?.previous_orders != null
                     ? `prev ${fmtINR(summary.previous_orders)}` : null}
                   testid="kpi-orders" />
              <Kpi icon={ChartLineUp} label="AOV"
                   value={`₹${fmtINR(kpis.aov)}`}
                   delta={<DeltaPill
                     direction={summary?.aov_change_direction}
                     pct={summary?.aov_change_pct}
                     title={summary?.previous_avg_order_value != null
                       ? `prev ₹${fmtINR(summary.previous_avg_order_value)}` : null}
                     testId="kpi-aov-delta" />}
                   sub={summary?.previous_avg_order_value
                     ? `prev ₹${fmtINR(summary.previous_avg_order_value)}` : null}
                   testid="kpi-aov" />
              <Kpi icon={Users} label="Covers"
                   value={fmtINR(kpis.covers)}
                   delta={<DeltaPill
                     direction={summary?.covers_change_direction}
                     pct={summary?.covers_change_pct}
                     title={summary?.previous_total_covers != null
                       ? `prev ${summary.previous_total_covers}` : null}
                     testId="kpi-covers-delta" />}
                   sub={kpis.orders > 0
                     ? `${(kpis.covers / kpis.orders).toFixed(1)} per order`
                     : null}
                   testid="kpi-covers" />
              <Kpi icon={Handshake} label="Contribution"
                   value={kpis.contribution == null ? '—' : fmtPct(kpis.contribution)}
                   delta={kpis.contribution == null ? null : (
                     <PpPill
                       pp={summary?.contribution_change_pp}
                       direction={summary?.contribution_change_direction}
                       title={summary?.previous_contribution_pct != null
                         ? `prev ${fmtPct(summary.previous_contribution_pct)}` : null}
                       testid="kpi-contribution-delta" />)}
                   sub={kpis.contribution == null
                     ? 'all partners view'
                     : summary?.previous_contribution_pct != null
                       ? `prev ${fmtPct(summary.previous_contribution_pct)}`
                       : 'of outlet revenue'}
                   testid="kpi-contribution" />
            </div>
          </div>

          {/* ── B + C row: Channel Mix + Time Intelligence ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* B. Channel Mix */}
            <div data-testid="partner-intel-channel-mix">
              <SectionHeader title="Channel Mix" sub="by revenue" />
              {summary?.narrative_channel && (
                <div className="text-[11px] text-zinc-400 italic mb-2"
                     data-testid="partner-intel-channel-narrative">
                  {summary.narrative_channel}
                </div>
              )}
              {channelMix.length === 0 ? (
                <div className="text-xs text-zinc-500 py-8 text-center">No mode breakdown.</div>
              ) : (
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                  <div style={{ height: 160 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={channelMix} dataKey="value" nameKey="name"
                             innerRadius={45} outerRadius={70} paddingAngle={2}
                             stroke="rgba(0,0,0,0.4)">
                          {channelMix.map((c, i) => (
                            <Cell key={i} fill={c.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ background: '#0a0a0a', border: '1px solid #27272a',
                                          borderRadius: 6, fontSize: 11 }}
                          formatter={(v, _, p) => [`₹${fmtINR(v)} · ${p.payload.share_pct}%`, p.payload.name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 space-y-1">
                    {channelMix.map(c => (
                      <div key={c.mode}
                           className="flex items-center justify-between text-[11px]"
                           data-testid={`channel-mix-row-${c.mode}`}>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full" style={{ background: c.fill }} />
                          <span className="text-zinc-300">{c.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 font-mono text-zinc-400">
                          <span>₹{fmtShort(c.value)} · {c.share_pct}%</span>
                          {c.revenue_change_direction && (
                            <DeltaPill
                              direction={c.revenue_change_direction}
                              pct={c.revenue_change_pct}
                              title={c.previous_net_sales != null
                                ? `prev ₹${fmtINR(c.previous_net_sales)}` : null}
                              testId={`channel-mix-delta-${c.mode}`} />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* C. Time Intelligence */}
            <div className="lg:col-span-2" data-testid="partner-intel-time">
              <SectionHeader
                title="Time Intelligence"
                sub={peakInfo
                  ? `Peak ${hourLabel(peakInfo.peak_hour)} · Best ${hourLabel(peakInfo.window_start)}–${hourLabel(peakInfo.window_end)}`
                  : 'No hourly data'}
              />
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                {loading ? (
                  <div className="text-xs text-zinc-500 py-8 text-center">Loading…</div>
                ) : (
                  <>
                    {timeNarrative && (
                      <div className="text-[11px] text-zinc-400 italic mb-2"
                           data-testid="partner-intel-time-narrative">
                        {timeNarrative}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <Kpi icon={Clock} label="Peak Hour"
                           value={peakInfo ? hourLabel(peakInfo.peak_hour) : '—'}
                           delta={peakInfo && prevPeakInfo ? (
                             <DeltaPill
                               direction={
                                 prevPeakInfo.peak_hour_orders === 0
                                   ? (peakInfo.peak_orders > 0 ? 'new' : 'flat')
                                   : (peakInfo.peak_orders > prevPeakInfo.peak_hour_orders + 0.5 ? 'up'
                                     : peakInfo.peak_orders < prevPeakInfo.peak_hour_orders - 0.5 ? 'down'
                                     : 'flat')
                               }
                               pct={prevPeakInfo.peak_hour_orders > 0
                                 ? Math.round((peakInfo.peak_orders - prevPeakInfo.peak_hour_orders)
                                              / prevPeakInfo.peak_hour_orders * 1000) / 10
                                 : null}
                               title={`prev ${prevPeakInfo.peak_hour_orders} orders at this hour`}
                               testId="time-peak-hour-delta" />
                           ) : null}
                           sub={peakInfo
                             ? `${peakInfo.peak_orders} orders${prevPeakInfo
                                 ? ` · prev ${prevPeakInfo.peak_hour_orders}` : ''}`
                             : null}
                           testid="time-peak-hour" />
                      <Kpi icon={Clock} label="Peak 3h Window"
                           value={peakInfo
                             ? `${hourLabel(peakInfo.window_start)}–${hourLabel(peakInfo.window_end)}`
                             : '—'}
                           delta={peakInfo && prevPeakInfo ? (
                             <DeltaPill
                               direction={
                                 prevPeakInfo.window_orders === 0
                                   ? (peakInfo.window_orders > 0 ? 'new' : 'flat')
                                   : (peakInfo.window_orders > prevPeakInfo.window_orders + 0.5 ? 'up'
                                     : peakInfo.window_orders < prevPeakInfo.window_orders - 0.5 ? 'down'
                                     : 'flat')
                               }
                               pct={prevPeakInfo.window_orders > 0
                                 ? Math.round((peakInfo.window_orders - prevPeakInfo.window_orders)
                                              / prevPeakInfo.window_orders * 1000) / 10
                                 : null}
                               title={`prev ${prevPeakInfo.window_orders} orders in this window`}
                               testId="time-peak-window-delta" />
                           ) : null}
                           sub={peakInfo
                             ? `${peakInfo.window_orders} orders${prevPeakInfo
                                 ? ` · prev ${prevPeakInfo.window_orders}` : ''}`
                             : null}
                           testid="time-peak-window" />
                    </div>

                    {/* ── Hour-of-day filter ──────────────────────
                        Operator picks a from/to hour window. Metrics
                        below + chart highlight update instantly from
                        the already-fetched hourly data — no extra
                        backend call. */}
                    {peakInfo && (
                      <div className="rounded-md border border-white/[0.06] bg-black/20 p-2.5 mb-3"
                           data-testid="hour-window-filter">
                        <div className="flex flex-wrap items-center gap-2 text-[11px]">
                          <span className="uppercase tracking-wider text-zinc-500 font-medium">
                            Time slot
                          </span>
                          <select
                            value={hourStart ?? ''}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              setHourStart(v);
                              if (hourEnd != null && hourEnd < v) setHourEnd(v);
                              setHourFilterActive(true);
                            }}
                            className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-zinc-200 font-mono focus:outline-none focus:border-cyan-500/50"
                            data-testid="hour-start-select"
                          >
                            {Array.from({ length: 24 }, (_, h) => (
                              <option key={h} value={h}>{hourLabel(h)}</option>
                            ))}
                          </select>
                          <span className="text-zinc-500">→</span>
                          <select
                            value={hourEnd ?? ''}
                            onChange={(e) => {
                              setHourEnd(parseInt(e.target.value, 10));
                              setHourFilterActive(true);
                            }}
                            className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-zinc-200 font-mono focus:outline-none focus:border-cyan-500/50"
                            data-testid="hour-end-select"
                          >
                            {Array.from({ length: 24 }, (_, h) => (
                              <option key={h} value={h} disabled={hourStart != null && h < hourStart}>
                                {hourLabel(h)}
                              </option>
                            ))}
                          </select>
                          {!hourFilterActive ? (
                            <button
                              onClick={() => setHourFilterActive(true)}
                              className="ml-1 px-2.5 py-1 rounded bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 border border-cyan-500/20 text-[11px] font-medium transition-colors"
                              data-testid="hour-window-apply"
                            >
                              Apply
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setHourFilterActive(false);
                                if (peakInfo) {
                                  setHourStart(peakInfo.window_start);
                                  setHourEnd(peakInfo.window_end - 1);
                                }
                              }}
                              className="ml-1 px-2.5 py-1 rounded bg-white/[0.04] hover:bg-white/[0.08] text-zinc-400 border border-white/10 text-[11px] transition-colors"
                              data-testid="hour-window-reset"
                            >
                              Reset
                            </button>
                          )}
                        </div>

                        {/* Result KPIs — only render when a window is applied */}
                        {windowMetrics && (
                          <div className="grid grid-cols-3 gap-2 mt-2.5" data-testid="window-metrics">
                            <Kpi icon={Receipt} label="Orders"
                                 value={
                                   <span className="inline-flex items-baseline gap-1.5">
                                     <span>{fmtINR(windowMetrics.orders)}</span>
                                     <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/20 font-mono"
                                           data-testid="window-orders-pct">
                                       {fmtPct(windowMetrics.orders_share_pct, 1)}
                                     </span>
                                   </span>
                                 }
                                 sub={`of total · ${windowMetrics.hours} hour${windowMetrics.hours === 1 ? '' : 's'}`}
                                 testid="window-orders" />
                            <Kpi icon={CurrencyInr} label="Revenue"
                                 value={
                                   <span className="inline-flex items-baseline gap-1.5">
                                     <span>₹{fmtShort(windowMetrics.revenue)}</span>
                                     <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/20 font-mono"
                                           data-testid="window-revenue-pct">
                                       {fmtPct(windowMetrics.revenue_share_pct, 1)}
                                     </span>
                                   </span>
                                 }
                                 sub={`of total · ₹${fmtINR(windowMetrics.revenue)}`}
                                 testid="window-revenue" />
                            <Kpi icon={ChartLineUp} label="AOV"
                                 value={windowMetrics.orders > 0 ? `₹${fmtINR(windowMetrics.aov)}` : '—'}
                                 sub={windowMetrics.orders === 0 ? 'no orders in slot' : null}
                                 testid="window-aov" />
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ height: 140 }} data-testid="time-hourly-chart">
                      <ResponsiveContainer>
                        <BarChart data={hourly} margin={{ top: 6, right: 8, left: -10, bottom: 0 }}>
                          <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                          <XAxis dataKey="hour" tickFormatter={(h) => h % 3 === 0 ? `${h}` : ''}
                                 axisLine={false} tickLine={false}
                                 tick={{ fill: '#71717a', fontSize: 10 }} />
                          <YAxis axisLine={false} tickLine={false}
                                 tick={{ fill: '#71717a', fontSize: 10 }} width={28} />
                          <Tooltip
                            contentStyle={{ background: '#0a0a0a', border: '1px solid #27272a',
                                            borderRadius: 6, fontSize: 11 }}
                            labelFormatter={(h) => `${hourLabel(h)}`}
                            formatter={(v, k) => k === 'orders'
                              ? [v, 'Orders']
                              : [`₹${fmtINR(v)}`, 'Revenue']}
                          />
                          <Bar dataKey="orders" radius={[3, 3, 0, 0]}>
                            {hourly.map((h) => {
                              // Highlight bars inside the chosen window;
                              // dim bars outside so the slot reads at a glance.
                              const inWindow = hourFilterActive
                                && hourStart != null && hourEnd != null
                                && h.hour >= hourStart && h.hour <= hourEnd;
                              const fill = hourFilterActive
                                ? (inWindow ? '#22d3ee' : 'rgba(34,211,238,0.20)')
                                : '#22d3ee';
                              return <Cell key={h.hour} fill={fill} />;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── D + E row: Top Items + Revenue Quality ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* D. Top Items */}
            <div data-testid="partner-intel-items">
              <SectionHeader title="Top Items" sub="by revenue" />
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                {loading ? (
                  <div className="text-xs text-zinc-500 py-6 text-center">Loading…</div>
                ) : topItems.length === 0 ? (
                  <div className="text-xs text-zinc-500 py-6 text-center">No item data for this partner.</div>
                ) : (
                  <div className="space-y-2">
                    {topItems.map((it, idx) => {
                      // /sales/items/top with include_comparison attaches
                      // quantity-based deltas (units sold) — items are
                      // tracked by quantity in the platform contract.
                      // Revenue ranks the list, qty drives the pill.
                      const dir = it.quantity_change_direction;
                      const pct = it.quantity_change_pct;
                      return (
                        <div key={`${it.item_name_normalized}-${idx}`}
                             className="flex items-center justify-between text-xs"
                             data-testid={`top-item-${idx}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-zinc-500 font-mono text-[10px] w-4">{idx + 1}.</span>
                            <span className="text-zinc-200 truncate">{it.item_name}</span>
                          </div>
                          <div className="flex items-center gap-2.5 shrink-0">
                            <span className="font-mono text-zinc-400">₹{fmtShort(it.net_sales)}</span>
                            {dir && (
                              <DeltaPill
                                direction={dir}
                                pct={pct}
                                title={it.previous_quantity != null
                                  ? `prev ${it.previous_quantity} units sold`
                                  : 'no sales in previous period'}
                                testId={`top-item-${idx}-delta`} />
                            )}
                            <span className="text-cyan-400/80 text-[10px] font-mono w-10 text-right">
                              {fmtPct(it.revenue_contribution_pct)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* E. Revenue Quality */}
            <div data-testid="partner-intel-quality">
              <SectionHeader title="Revenue Quality" />
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Kpi icon={Tag} label="Discount Rate"
                       value={fmtPct(kpis.discount_rate_pct, 1)}
                       delta={<PpPill
                         pp={summary?.discount_rate_change_pp}
                         direction={summary?.discount_rate_change_direction}
                         title={summary?.previous_discount_rate_pct != null
                           ? `prev ${fmtPct(summary.previous_discount_rate_pct)}` : null}
                         testid="kpi-discount-rate-delta" />}
                       sub={summary?.previous_discount_rate_pct != null
                         ? `₹${fmtINR(kpis.discounts)} foregone · prev ${fmtPct(summary.previous_discount_rate_pct)}`
                         : `₹${fmtINR(kpis.discounts)} foregone`}
                       testid="kpi-discount-rate" />
                  <Kpi icon={CurrencyInr} label="Gross / Net"
                       value={`₹${fmtShort(kpis.gross_sales)} / ₹${fmtShort(kpis.revenue)}`}
                       delta={<DeltaPill
                         direction={summary?.revenue_change_direction}
                         pct={summary?.revenue_change_pct}
                         title={summary?.previous_gross_sales != null
                           ? `prev ₹${fmtINR(summary.previous_gross_sales)} / ₹${fmtINR(summary.previous_net_sales)}` : null}
                         testId="kpi-gross-net-delta" />}
                       sub={summary?.previous_gross_sales != null
                         ? `prev ₹${fmtShort(summary.previous_gross_sales)} / ₹${fmtShort(summary.previous_net_sales)}`
                         : null}
                       testid="kpi-gross-net" />
                </div>
                {revenueTrend.length > 1 && (
                  <div data-testid="partner-intel-trend">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      Daily Revenue & AOV
                    </div>
                    <div style={{ height: 100 }}>
                      <ResponsiveContainer>
                        <LineChart data={revenueTrend} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                          <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                          <XAxis dataKey="date" hide />
                          <YAxis yAxisId="rev" axisLine={false} tickLine={false}
                                 tick={{ fill: '#71717a', fontSize: 9 }} width={32}
                                 tickFormatter={(v) => fmtShort(v)} />
                          <YAxis yAxisId="aov" orientation="right" axisLine={false} tickLine={false}
                                 tick={{ fill: '#a78bfa', fontSize: 9 }} width={28}
                                 tickFormatter={(v) => fmtShort(v)} />
                          <Tooltip
                            contentStyle={{ background: '#0a0a0a', border: '1px solid #27272a',
                                            borderRadius: 6, fontSize: 11 }}
                            formatter={(v, k) => [
                              `₹${fmtINR(v)}`,
                              k === 'revenue' ? 'Revenue' : 'AOV',
                            ]}
                          />
                          <Line yAxisId="rev"  type="monotone" dataKey="revenue"
                                stroke="#22d3ee" strokeWidth={1.6} dot={false} />
                          <Line yAxisId="aov"  type="monotone" dataKey="aov"
                                stroke="#a78bfa" strokeWidth={1.4} strokeDasharray="3 3" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-px bg-cyan-400" /> Revenue</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-px bg-violet-400 border-dashed" /> AOV</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerIntelligencePanel;
